export interface AccountTableItem {
  account_id: string;
  account_name: string;
  account_type: 'root' | 'sub';
  parent_account_id?: string;
}

export type AccountFilter = 'all' | 'root' | 'sub';
export type SortDirection = 'asc' | 'desc';

// Account Type Options
export type AccountType =
  | 'BASIC'
  | 'OLDLIMITED'
  | 'DAILYCREDITS'
  | 'SUBSCRIPTION'
  | 'MONTHLYCREDITS'
  | 'MONTHLYACCESS'
  | 'TRIAL';

// Re-export from constants for backward compatibility
export { ACCOUNT_TYPE_OPTIONS } from './constants';
export type { AccountTypeOption } from './constants';

export interface AccountsTableParams {
  filter: AccountFilter;
  search: string;
  sort: {
    column: string | null;
    direction: SortDirection;
  };
}

// API Response interfaces
export interface FetchGetSelfResponse {
  root_accounts: string[];
  last_name: string;
  user_type: 'USER' | 'ADMIN-USER' | 'SUPER-USER' | 'IDENTITY-USER';
  user_id: number;
  local_timezone: string;
  first_name: string;
  email: string;
  identities: string[];
  user_hash?: string;
}

// The API returns balance/credit_limit as a plain number, but earlier
// builds wrapped them as { source, parsedValue }. Accept both shapes and
// use parseAccountStatusValue() to read the numeric amount.
export type AccountStatusValue = number | { source: string; parsedValue: number };

export interface FetchAccountStatusResponse {
  account_type: string;
  tcr_identity_status: string | null;
  credit_limit: AccountStatusValue;
  balance: AccountStatusValue;
}

export const parseAccountStatusValue = (
  value: AccountStatusValue | undefined | null
): number | undefined => {
  if (typeof value === 'number') {
    return value;
  }
  return value?.parsedValue;
};

export interface FetchAccountDetailsResponse {
  per_vm_minute: string;
  can_use_sms: string;
  min_increment: string;
  can_use_10dlc: number;
  per_p2p_inbound_success: string;
  can_use_p2p: string;
  account_name: string;
  max_rvm_ports: string;
  per_lead: string;
  per_call_restricted: string;
  initial_canvasser_schedule_minimum_window: number;
  per_vm_success: string;
  notification_email: string;
  per_p2p_mms_success: string;
  is_1to1_unlimited: number;
  per_p2p_high_demand_initial_canvass: string;
  identity_only: string;
  per_p2p_video_mms_success: string;
  can_use_ringlessvm: string;
  max_ports: string;
  parent_account_id: string;
  per_survey_min_surcharge: string;
  identity_id: string;
  credit_limit: string;
  can_use_ai: string;
  per_dnc_scrub: string;
  per_xfer_restricted: string;
  warn_low_balance: string;
  is_prepay: string;
  can_use_ivr: string;
  can_use_nat_dnc: string;
  per_min_rate: string;
  is_value_added_reseller: string | null;
  per_p2p_video_mms_attempt: string;
  per_cv_submission: string;
  can_suppress_wireless: number;
  rvm_unlimited_package: number;
  can_suppress_dnc: number;
  per_on_demand: string;
  per_adv_rvm_success: string;
  has_presorted_leads: number;
  per_adv_rvm_attempt: string;
  force_cell_suppress: string;
  is_unlimited_trial: number;
  per_xfer_rate: string;
  can_use_restricted: string;
  can_schedule_initial_canvassers: number;
  account_id: string;
  from_email: string;
  per_sms_attempt: string;
  parent_account_key: string;
  per_sms_success: string;
  logo_small: string | null;
  per_call_rate: string;
  can_minimize_sms: string;
  branding_logo: string | null;
  start_date: string;
  sms_round_increment: string;
  can_use_classic: number;
  can_auto_check_lists: number;
  round_increment: string;
  branding_enabled: string;
  per_p2p_mms_unregistered: string;
  ivr_unlimited_package: number;
  can_share_lists: string;
  can_use_state_cid: string;
  is_ivr_unlimited: number;
  ui_mode: number;
  branding_url: string | null;
  per_tracked_link: string;
  warn_low_balance_email: string;
  enable_list_mapping: string;
  end_date: string;
  per_tracked_link_click: string;
  max_identities: string;
  per_p2p_unregistered: string;
  per_p2p_mms_attempt: string;
  per_p2p_initial_canvass: string;
  is_user_root: number;
  can_suppress_state_dnc: number;
  is_sms_unlimited: number;
  per_vm_attempt: string;
  can_use_adv_rvm: string;
  subscription_id: string | null;
  branding_base_domain: string | null;
  max_sms_per_min: string;
  sms_min_increment: string;
  unlimited_package: number;
  is_rvm_unlimited: number;
  can_use_video_mms: string | null;
  per_recording: string;
  account_type: string;
  per_p2p_mms_inbound_success: string;
  per_p2p_attempt: string;
  per_p2p_success: string;
  per_min_restricted: string;
  p2p_rateplan?: P2PRateplan;
}

export interface P2PRateplanRate {
  label: string;
  per_sms_success: number;
  per_sms_inbound_success: number;
  per_mms_success: number;
  per_mms_inbound_success: number;
}

export interface P2PRateplan {
  default_label: string;
  default_per_sms_success: number;
  default_per_sms_inbound_success: number;
  default_per_mms_success: number;
  default_per_mms_inbound_success: number;
  rates: P2PRateplanRate[];
}

export interface FetchSubAccountsResponse {
  subs: {
    account_name: string;
    account_id: string;
  }[];
}

export interface FetchStatsResponse {
  smsStats: {
    delivered: number | null;
    num_campaigns: number;
  };
  ivrStats: {
    ringing: number | null;
    ports: number | null;
    minutes: number | null;
    xfer: number | null;
    connected: number | null;
    calls: number | null;
  };
  p2pStats: {
    daily_remaining_contacts: number;
    daily_conversations: number;
    job_count: number;
    daily_total: number;
    active_canvassers: number;
  };
  rvmStats: {
    initiating: number;
    success: number;
    ports: number;
    delivering: number;
  };
}

// API Request interfaces
export interface FetchAccountDetailsParams {
  accountNumber: string;
}

export interface FetchAccountStatusParams {
  accountNumber: string;
}

export interface FetchSubAccountsParams {
  rootAccountId: string;
}

// Type aliases for commonly used types
export type AccountDetails = FetchAccountDetailsResponse;
export type AllStats = FetchStatsResponse;

// Assigned Users Types
export interface AssignedUser {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  user_type: 'USER' | 'ADMIN-USER' | 'SUPER-USER' | 'IDENTITY-USER';
  root_accounts?: string[];
}

export interface FetchAssignedUsersResponse extends Array<AssignedUser> {}

export interface FetchAssignedUsersParams {
  accountId: string;
}

export interface AssignedUsersTableItem {
  user_id: string;
  display_name: string;
  email: string;
  user_type: 'USER' | 'ADMIN-USER' | 'SUPER-USER' | 'IDENTITY-USER';
}

// Accounting/Transactions Types
// Credit transaction from API
export interface Credit {
  reference_number: string;
  credit_amount: string;
  identity_id: string;
  credit_id: string;
  payment_type: string;
  account_id: string;
  payment_by: string;
  credit_date: string; // ISO timestamp
  notes: string;
}

// Debit transaction from API
export interface Debit {
  debit_id: string;
  debit_date: string; // Date string
  account_id: string;
  identity_id: string;
  debit_amount: string;
  debit_type: string;
  notes: string | null;
  leads_loaded?: number;
  cv_submissions?: number;
  p2p?: number;
  p2p_attempts?: number;
  p2p_unregistered?: number;
  p2p_mmss?: number;
  p2p_mms_attempts?: number;
  p2p_mms_unregistered?: number;
  p2p_video_mmss?: number;
  p2p_video_mms_attempts?: number;
  tracked_links?: number;
  tracked_link_clicks?: number;
  p2p_initial_canvasses?: number;
  p2p_high_demand_initial_canvasses?: number;

  // Other fields exist but not needed for table
  p2p_rateplan_adjusted?: number;
  p2p_mms_rateplan_adjusted?: number;
  sms_attempts?: number;
  seconds?: number;
  calls?: number;
  ringlessvm_attempts?: number;
  adv_ringlessvms?: number;
  ringlessvms?: number;
  xfer_seconds?: number;
  adv_ringlessvm_attempts?: number;
  xfers?: number;
  on_demand?: number;
  sms?: number;
  recordings?: number | null;
  survey_seconds?: number;
}

// Base transaction properties shared by both credits and debits
interface BaseAccountTransaction {
  id: string;
  transaction_date: Date; // For sorting
  formatted_date: string; // mm/dd/yyyy for display
  type_label: string; // Display label for badge
  applied_by: string;
  amount: number;
  balance: number; // Calculated field
  reference: string;
  notes: string;
}

// Unified transaction for table display - discriminated union
export type AccountTransaction =
  | (BaseAccountTransaction & {
      type: 'credit';
    })
  | (BaseAccountTransaction & {
      type: 'debit';
      leads_loaded?: number;
      cv_submissions?: number;
      p2p?: number; // SMS Outbounds
      p2p_attempts?: number; // SMS Outbound Attempts
      p2p_unregistered?: number; // Unregistered SMS
      p2p_mmss?: number; // MMS Outbounds
      p2p_mms_attempts?: number; // MMS Outbound Attempts
      p2p_mms_unregistered?: number; // Unregistered MMS
      p2p_video_mmss?: number; // Video MMS
      p2p_video_mms_attempts?: number; // Video MMS Attempts
      tracked_links?: number;
      tracked_link_clicks?: number;
      p2p_initial_canvasses?: number; // Initial Canvasses
      p2p_high_demand_initial_canvasses?: number; // High Demand Initial Canvasses
      p2p_rateplan_adjusted?: number;
      p2p_mms_rateplan_adjusted?: number;
    });

// Filter and params types
export type TransactionFilter = 'all' | 'credits' | 'debits';
export type DateRangeOption =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'thisYear'
  | 'lastYear';

// Add Credit API Types
export interface AddCreditPayload {
  account_id: string;
  credit_amount: number;
  notes: string;
  payment_by: string;
  payment_type: string;
  reference_number: string;
}

export interface AddCreditResponse {
  success: boolean;
  credit_id?: string;
  message?: string;
}

export interface AssignUserToAccountResponse {
  success: boolean;
  message?: string;
}

export interface UnassignUserFromAccountResponse {
  success: boolean;
  message?: string;
}
